<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-arrow">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{ slug ? "" : "Registrar categoría" }}
                    <strong v-if="slug" class="text-theme-primary">
                        {{ itemComponent.name }}
                    </strong>
                </h1>
                <div v-if="slug" class="flex items-center gap-2">
                    <strong class="text-sm text-theme-muted">
                        Slug: {{ itemComponent.slug }}
                    </strong>
                    <span
                        class="bg-theme-primary text-theme-inverse rounded-full px-3 py-1 text-xs font-semibold"
                    >
                        {{
                            itemComponent.parentSlug
                                ? "Subcategoría"
                                : "Principal"
                        }}
                    </span>
                </div>
                <wiki-learn-more-link topic="lineas-y-sublineas" class="mt-2" />
            </div>
            <div
                v-if="slug"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small> 📆💾 {{ itemComponent.createdAt | date }} </small>
                <small> 📆✏ {{ itemComponent.updatedAt | date }} </small>
            </div>
        </div>

        <div class="my-4">
            <custom-navbar
                id="tabs-sublines"
                v-model="activeTab"
                center-collapsed
                not-line
            >
                <template #left>
                    <custom-navbar-item id="home">
                        <router-link
                            class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                            to="/app/lines"
                        >
                            <span>🏠</span>
                        </router-link>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="general"
                        :active="['general', 'home'].includes(activeTab)"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> General </span>
                            <span>&nbsp;⚙️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="
                            $ability.hasPermission('line:Get') &&
                            $enabledEcommerceOptions &&
                            !isCreation
                        "
                        id="seo"
                        :active="activeTab === 'seo'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Contenido&nbsp;
                            </span>
                            <span>&nbsp;📒</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="
                            $ability.hasPermission('line:Get') &&
                            $enabledEcommerceOptions &&
                            !isCreation &&
                            itemComponent.active
                        "
                        id="filters"
                        :active="activeTab === 'filters'"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Filtros </span>
                            <span>&nbsp;🔍</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="
                            slug &&
                            $ability.hasPermission('line:Get') &&
                            $enabledEcommerceOptions &&
                            itemComponent.active
                        "
                        id="collections"
                        :active="activeTab === 'collections'"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Colecciones y marcas
                            </span>
                            <span>&nbsp;🏷️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="
                            slug &&
                            $ability.hasPermission('reference:ListHighlight') &&
                            itemComponent.active
                        "
                        id="highlight-references"
                        :active="activeTab === 'highlight-references'"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Referencias destacadas
                            </span>
                            <span>&nbsp;⭐</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug && $ability.hasPermission('line:Get')"
                        id="advance"
                        :active="activeTab === 'advance'"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Avanzado </span>
                            <span>&nbsp;🤖</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug && $ability.hasPermission('subline:List')"
                        id="sublines"
                        :active="activeTab === 'sublines'"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Sublíneas </span>
                            <span>&nbsp;➖</span>
                        </div>
                    </custom-navbar-item>
                </template>
                <template #right>
                    <div class="hidden md:block">
                        <custom-switch
                            v-if="$ability.hasPermission('line:Update') && slug"
                            v-model="enabledUpdate"
                        >
                            <span class="text-theme-inverse">🖊</span>
                        </custom-switch>
                    </div>
                </template>
            </custom-navbar>
        </div>

        <div class="flex justify-center">
            <div class="block md:hidden my-3">
                <custom-switch
                    v-if="$ability.hasPermission('line:Update') && !isCreation"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div v-show="['general', 'home'].includes(activeTab)" id="generalTab">
            <div v-if="slug" class="flex flex-wrap">
                <div
                    v-if="$enabledEcommerceOptions"
                    class="w-full mt-3 flex items-center"
                >
                    <active-selector
                        v-model="itemComponent.active"
                        :enabled="enabledUpdate"
                        :public-url="publicLineUrl"
                        :public-label="publicLineLabel"
                        on-label="Publicado"
                        off-label="Oculto"
                    />
                    <div
                        v-if="itemComponent.active"
                        class="flex items-start h-full"
                    >
                        <custom-switch
                            v-if="enabledUpdate"
                            v-model="itemComponent.highlight"
                            success
                            class="min-w-24"
                        >
                            <template #on><small>Destacado</small></template>
                            <template #off><small>Común</small></template>
                        </custom-switch>
                        <span
                            v-else
                            class="text-sm font-medium"
                            :class="
                                itemComponent.highlight
                                    ? 'text-theme-success'
                                    : 'text-theme-muted'
                            "
                        >
                            {{
                                itemComponent.highlight ? "Destacado" : "Común"
                            }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div
                    class="w-full mt-5 px-2"
                    :class="isCreation ? 'w-full md:w-1/2' : 'w-full'"
                >
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName & introValidators.name
                                ? 'Nombre de categoría invalida'
                                : ''
                        "
                        @blur="blurName"
                    />
                </div>
                <div v-if="isCreation" class="w-full mt-5 md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug & introValidators.slug
                                ? 'Slug de categoría invalida'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    >
                        <template v-if="slug" #icon> 🧾 </template>
                    </custom-input>
                </div>

                <div class="w-full mt-2 px-2">
                    <custom-input
                        v-model="itemComponent.summary"
                        label="Descripción"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidShortDescription & introValidators.summary
                                ? 'Descripción invalida'
                                : ''
                        "
                        @blur="introValidators.summary = true"
                    />
                </div>

                <div class="w-full mt-2 px-2">
                    <line-selector
                        v-model="itemComponent.line"
                        show-label
                        :enabled="enabledUpdate"
                        :default-filters="{
                            active: true,
                            type: 'line'
                        }"
                    />
                </div>

                <div
                    class="flex flex-col items-start md:flex-row w-full gap-4 mt-8 px-2"
                >
                    <div class="w-full md:w-1/2">
                        <fragment v-if="$enabledEcommerceOptions">
                            <images-manage
                                :id="slug"
                                v-model="images"
                                type="categoría"
                                :enabled="enabledUpdate"
                                :default-alt="'Imagen de ' + itemComponent.name"
                                default-size="squareImage"
                            />
                        </fragment>
                    </div>
                </div>

                <div class="w-full mt-8">
                    <div
                        v-if="enabledUpdate"
                        class="w-full flex flex-col sm:flex-row justify-center"
                    >
                        <app-button
                            v-if="!slug"
                            class="w-full md:w-auto"
                            size="large"
                            color="success"
                            border
                            :loading="isLoading"
                            @click="createLineComponent(false)"
                        >
                            Crear categoría
                        </app-button>
                        <app-button
                            v-if="!slug"
                            class="w-full md:w-auto"
                            size="large"
                            color="success"
                            :loading="isLoading"
                            @click="createLineComponent(true)"
                        >
                            Crear y publicar
                        </app-button>
                        <app-button v-else @click="updateLineComponent">
                            Actualizar categoría
                        </app-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'seo'" id="seoTab" class="space-y-6">
            <div id="seo-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl font-semibold my-4">
                        Contenido de la categoría
                    </h2>

                    <span>
                        Este contenido es el que se va a mostrar en la página de
                        la categoría, es importante que este bien redactado y
                        sea atractivo para los usuarios, ya que esto va a
                        influir en la decisión de compra de los usuarios.
                    </span>
                </div>
            </div>

            <div class="bg-theme-card rounded-2xl shadow-sm p-5 mt-5">
                <h2
                    class="text-theme-primary text-xl font-semibold mb-4 flex items-center gap-2"
                >
                    Contenido de la categoría
                </h2>
                <div v-if="$enabledEcommerceOptions">
                    <markdown-editor
                        v-model="itemComponent.content"
                        class="mt-2"
                        :enabled="enabledUpdate"
                    />
                </div>
            </div>
            <div
                v-if="$enabledEcommerceOptions"
                class="bg-theme-card rounded-2xl shadow-sm p-5"
            >
                <h2
                    class="text-theme-primary text-xl font-semibold mb-4 flex items-center gap-2"
                >
                    Palabras clave
                </h2>
                <keywords-manage
                    v-model="itemComponent.keywords"
                    :base="$data.$keywordsBase"
                    :enabled="enabledUpdate"
                />
            </div>
            <div v-if="enabledUpdate" class="w-full flex justify-center">
                <app-button
                    :loading="isLoadingContent"
                    @click="updateLineContentComponent"
                >
                    Actualizar Contenido
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'filters'" id="filtersTab">
            <div id="filters-container">
                <div id="filters-title" class="flex flex-wrap">
                    <div class="flex flex-col">
                        <h2
                            class="text-theme-primary text-2xl font-semibold mt-4"
                        >
                            Filtros
                        </h2>
                        <span> Configura los filtros de la categoría </span>
                    </div>
                </div>
                <div class="flex flex-wrap mt-4">
                    <filters-manage
                        v-model="itemComponent.filters"
                        :enabled="enabledUpdate"
                    >
                        <template slot="action">
                            <app-button
                                :loading="isLoadingFilters"
                                class="mt-4"
                                @click="updateLineFiltersComponent()"
                            >
                                Actualizar Filtros
                            </app-button>
                        </template>
                    </filters-manage>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'collections'" id="routesTab">
            <div id="brands-container">
                <div id="brands-title" class="flex flex-wrap">
                    <div class="flex flex-col">
                        <h2
                            class="text-theme-primary text-2xl font-semibold mt-4"
                        >
                            Marcas
                        </h2>
                        <span>
                            Las marcas que saldran en la página de la categoría.
                        </span>
                    </div>
                </div>
                <div class="flex flex-wrap">
                    <brands-manage
                        v-model="itemComponent.brands"
                        :enabled="enabledUpdate"
                        :default-filters="{
                            active: itemComponent.active
                        }"
                    >
                        <template slot="action">
                            <app-button
                                :loading="isLoadingBrands"
                                class="mt-4"
                                @click="updateBrandsLine()"
                            >
                                Actualizar Marcas
                            </app-button>
                        </template>
                    </brands-manage>
                </div>
            </div>
            <div id="collections-container">
                <div id="collections-title" class="flex flex-wrap">
                    <div class="flex flex-col">
                        <h2
                            class="text-theme-primary text-2xl font-semibold mt-4"
                        >
                            Colecciones
                        </h2>
                        <span>
                            Las colecciones determinan los productos que seran
                            cargados despues de la carga inicial del producto.
                            Los productos pueden agruparse por el personaje
                            principal, por la serie o anime, el colegio del
                            producto o cualquier característica que permita
                            agrupar a productos de diferentes categorías.
                        </span>
                    </div>
                </div>
                <div class="flex flex-wrap">
                    <collections-manage
                        v-model="itemComponent.collections"
                        :enabled="enabledUpdate"
                        :default-filters="{
                            active: true
                        }"
                    >
                        <template slot="action">
                            <app-button
                                :loading="isLoadingCollections"
                                class="mt-4"
                                @click="updateCollectionsLine()"
                            >
                                Actualizar Colecciones
                            </app-button>
                        </template>
                    </collections-manage>
                </div>
            </div>
        </div>

        <div
            v-if="activeTab === 'highlight-references'"
            id="highlightReferencesTab"
        >
            <div id="highlight-references-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl font-semibold mt-4">
                        Referencias destacadas
                    </h2>
                    <span>
                        Las variantes que se muestran como destacadas para esta
                        categoría.
                    </span>
                </div>
            </div>
            <div class="flex flex-wrap mt-4 w-full">
                <div class="w-full md:w-1/2">
                    <reference-highlight-type-selector
                        v-model="highlightReferencesType"
                        enabled
                        label="Tipo de destacado"
                    />
                </div>
            </div>

            <div v-if="enabledUpdate" class="mt-6 w-full">
                <references-search-scanner
                    :default-filters="{ active: true }"
                    enabled-scanner
                    @input="pushHighlightReference"
                />
            </div>

            <div
                v-if="!highlightReferences.length"
                class="mt-4 py-10 text-center text-theme-muted w-full"
            >
                Esta categoría todavía no tiene referencias destacadas para este
                tipo.
            </div>
            <div v-else class="mt-4 w-full">
                <references-cards
                    v-model="highlightReferences"
                    :enabled="enabledUpdate"
                />
            </div>

            <div v-if="enabledUpdate" class="flex justify-end mt-4 mb-6 w-full">
                <app-button
                    :loading="isLoadingHighlightReferences"
                    color="success"
                    @click="updateHighlightReferencesLine()"
                >
                    Actualizar Referencias Destacadas
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'advance'" id="advanceTab">
            <div id="advance-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl font-semibold mt-4">
                        Avanzado
                    </h2>
                    <span>
                        Puedes eliminar la categoría aquí, ten mucho cuidado.
                    </span>
                </div>
            </div>
            <div class="flex justify-around mt-5">
                <app-button
                    v-if="$ability.hasPermission('line:Delete') && slug"
                    danger
                    @click="deleteLineComponent"
                >
                    Eliminación
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'sublines'" id="sublinesTab">
            <div id="sublines-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl font-semibold mt-4">
                        Sublíneas
                    </h2>
                    <span>
                        Las sublíneas que pertenecen a esta categoría.
                    </span>
                </div>
            </div>
            <div class="flex flex-wrap mt-4">
                <custom-table
                    :fields="sublinesFields"
                    :items="sublines"
                    items-empty-message="Esta categoría todavía no tiene sublíneas registradas."
                    class="w-full"
                >
                    <template #cell(slug)="data">
                        <router-link
                            :to="`/app/sublines/detail/${data.item.slug}`"
                            class="flex flex-col items-center font-mono rounded-full w-min-16 px-1 py-1 text-white"
                            :class="
                                data.item.active
                                    ? 'bg-theme-success'
                                    : 'bg-theme-disabled'
                            "
                        >
                            <span class="text-xs font-mono">
                                {{ data.item.slug }}
                            </span>
                        </router-link>
                    </template>
                    <template #cell(active)="data">
                        <span
                            class="text-sm font-medium"
                            :class="
                                data.item.active
                                    ? 'text-theme-success'
                                    : 'text-theme-muted'
                            "
                        >
                            {{ data.item.active ? "Publicado" : "Oculto" }}
                        </span>
                    </template>
                    <template #cell(updatedAt)="data">
                        <small class="text-center">
                            {{ data.item.updatedAt | date }}
                        </small>
                    </template>
                </custom-table>
            </div>
        </div>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { slugRegex, summaryRegex, titleRegex } from "@/common/lib/regex";
import { slugify } from "@/common/lib/transformers";
import { stripUrlProtocol } from "@/common/lib/url";

import BrandsManage from "@/components/brands/BrandsManage.vue";
import CollectionsManage from "@/components/collections/CollectionsManage.vue";
import LineSelector from "@/components/lines/LineSelector.vue";
import ReferenceHighlightTypeSelector from "@/components/products/ReferenceHighlightTypeSelector.vue";
import ReferencesCards from "@/components/references/ReferencesCards.vue";
import ReferencesSearchScanner from "@/components/references/ReferencesSearchScanner.vue";
import ActiveSelector from "@/components/utils/ActiveSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import FiltersManage from "@/components/utils/FiltersManage.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import KeywordsManage from "@/components/utils/KeywordsManage.vue";
import MarkdownEditor from "@/components/utils/MarkdownEditor.vue";
import WikiLearnMoreLink from "@/components/utils/WikiLearnMoreLink.vue";

export default {
    name: "CreateUpdateLine",
    components: {
        BrandsManage,
        CollectionsManage,
        LineSelector,
        ReferenceHighlightTypeSelector,
        ReferencesCards,
        ReferencesSearchScanner,
        ImagesManage,
        KeywordsManage,
        FiltersManage,
        MarkdownEditor,
        WikiLearnMoreLink,
        ActiveSelector,
        CustomInput,
        CustomTable
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        slug: "",
        images: [],
        loadItem: false,
        isCreation: false,
        isLoading: false,
        isLoadingCollections: false,
        isLoadingBrands: false,
        enabledUpdate: false,
        activeTab: "general",
        $tabsWhitelist: [
            "general",
            "seo",
            "filters",
            "collections",
            "highlight-references",
            "advance",
            "sublines"
        ],
        introValidators: { slug: false, name: false, summary: false },
        isLoadingFilters: false,
        isLoadingContent: false,
        seoExpanded: false,
        sublines: [],
        isLoadingSublines: false,
        highlightReferences: [],
        highlightReferencesType: "highlight",
        isLoadingHighlightReferences: false,
        itemComponent: {
            name: "",
            content: "",
            slug: "",
            summary: "",
            keywords: [],
            images: [],
            filters: {},
            line: { slug: "", name: "" },
            parentSlug: null,
            updatedAt: "",
            createdAt: "",
            highlight: false,
            highlightType: null,
            active: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidName() {
            return titleRegex.test(this.itemComponent.name);
        },
        isValidShortDescription() {
            return summaryRegex.test(this.itemComponent.summary);
        },
        publicLineUrl() {
            if (!this.itemComponent?.slug || !this.$publicUrl) {
                return "";
            }
            return `${this.$publicUrl}/lines/${this.itemComponent.slug}`;
        },
        publicLineLabel() {
            return stripUrlProtocol(this.publicLineUrl);
        },
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        },
        sublinesFields() {
            return [
                { key: "slug", label: "Slug", sortable: false },
                { key: "name", label: "Nombre", sortable: false },
                { key: "active", label: "Publicado", sortable: false },
                { key: "updatedAt", label: "📆 Actualización", sortable: false }
            ];
        }
    },
    watch: {
        "$route.params.slug"() {
            this.resetData();
            this.initComponent();
        },
        "$route.params.slugClone"() {
            this.resetData();
            this.initComponent();
        },
        activeTab(value) {
            if (value === "sublines" && this.slug) {
                this.loadSublines();
            }
            if (value === "highlight-references" && this.slug) {
                this.loadHighlightReferences();
            }
        },
        highlightReferencesType() {
            if (this.activeTab === "highlight-references" && this.slug) {
                this.loadHighlightReferences();
            }
        }
    },
    async mounted() {
        await this.initComponent();
    },
    methods: {
        ...mapActions("lines", [
            "getLineBySlug",
            "updateLine",
            "createLine",
            "deleteLine",
            "updateLineFilters",
            "updateLineBrands",
            "updateLineCollections"
        ]),
        ...mapActions("products", [
            "listHighlightReferences",
            "updateHighlightReferences"
        ]),
        ...mapActions("sublines", ["listSublinesByLine"]),
        resetData() {
            this.slug = "";
            this.images = [];
            this.loadItem = false;
            this.isLoading = false;
            this.isLoadingCollections = false;
            this.isLoadingBrands = false;
            this.enabledUpdate = false;
            this.activeTab = "general";
            this.introValidators = { slug: false, name: false, summary: false };
            this.isLoadingFilters = false;
            this.seoExpanded = false;
            this.sublines = [];
            this.isLoadingSublines = false;
            this.highlightReferences = [];
            this.highlightReferencesType = "highlight";
            this.isLoadingHighlightReferences = false;
            this.itemComponent = {
                name: "",
                content: "",
                slug: "",
                summary: "",
                keywords: [],
                images: [],
                filters: {},
                line: { slug: "", name: "" },
                parentSlug: null,
                updatedAt: "",
                createdAt: "",
                highlight: false,
                highlightType: null,
                active: false
            };
        },
        async initComponent() {
            const verifyPagePermission =
                this.$ability.verifyPagePermission.bind(this);
            this.isCreation = true;
            if ("slug" in this.$route.params) {
                this.isCreation = false;
                const slug = this.$route.params.slug;
                this.slug = slug;
                await this.getBaseItem(slug);
            } else if ("slugClone" in this.$route.params) {
                if (!(await verifyPagePermission("line:Create", "/app/lines")))
                    return;
                this.enabledUpdate = true;
                const slugTmp = this.$route.params.slugClone;
                this.slug = `${slugTmp}-clone`;
                await this.getBaseItem(slugTmp);
                this.itemComponent = {
                    ...this.itemComponent,
                    name: `${this.itemComponent.name} CLONE`,
                    slug: this.slug,
                    active: false,
                    highlight: false,
                    highlightType: null
                };
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Estas clonando una categoría",
                    icon: "info",
                    showCloseButton: true
                });
            } else {
                if (!(await verifyPagePermission("line:Create", "/app/lines")))
                    return;
                this.enabledUpdate = true;
                this.loadItem = true;
            }
        },
        async getBaseItem(slug) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const info = await this.getLineBySlug({ slug });
                this.itemComponent = {
                    ...info,
                    highlight: info.highlightType === "highlight"
                };
                this.images = this.itemComponent.images;
                this.loadItem = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/lines");
                }
            }
        },
        async updateLineFiltersComponent() {
            this.isLoadingBrands = true;
            try {
                await this.updateLineFilters({
                    lineSlug: this.itemComponent.slug,
                    filters: this.itemComponent.filters
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingBrands = false;
            } catch (error) {
                this.isLoadingBrands = false;
                this.isLoadingBrands = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateLineComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateLine(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createLineComponent(publish = false) {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createLine({
                    ...this.getDataSend(),
                    ...this.getContentDataSend(),
                    active: publish
                });
                this.$loading.hide();
                this.itemComponent = { ...data, ...this.itemComponent };
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Slug: ${data.slug}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de categorías",
                    cancelButtonText: "Registrar categoría"
                });
                if (dialogResult.isConfirmed) {
                    this.slug = data.slug;
                    this.isCreation = false;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/lines/detail/${this.slug}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/lines");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        slug: false,
                        name: false,
                        summary: false
                    };
                    this.itemComponent = {
                        name: "",
                        content: "",
                        slug: "",
                        summary: "",
                        keywords: [],
                        images: [],
                        line: { slug: "", name: "" },
                        parentSlug: null,
                        active: false,
                        highlight: false,
                        highlightType: null
                    };
                    this.images = [];
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async setSlug() {
            if (this.isCreation && this.isValidName) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "¿Deseas cambiar el slug?",
                    text: "Ten en cuenta que el slug solo puede definirse o modificarse durante la creación. Una vez guardado, no podrá ser cambiado posteriormente.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Sí, cambiar",
                    cancelButtonText: "Cancelar"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.name);
                }
            }
        },
        async updateCollectionsLine() {
            this.isLoadingCollections = true;
            try {
                await this.updateLineCollections({
                    lineSlug: this.itemComponent.slug,
                    collectionsSlugs: this.itemComponent.collections.map(
                        (item) => ({
                            slug: item.slug
                        })
                    )
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingCollections = false;
            } catch (error) {
                this.isLoadingCollections = false;
                this.isLoadingCollections = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateBrandsLine() {
            this.isLoadingFilters = true;
            try {
                await this.updateLineBrands({
                    lineSlug: this.itemComponent.slug,
                    brandsSlugs: this.itemComponent.brands.map((item) => ({
                        slug: item.slug
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingFilters = false;
            } catch (error) {
                this.isLoadingFilters = false;
                this.isLoadingFilters = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteLineComponent() {
            if (this.itemComponent.active) {
                return this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Categoría está activa",
                    text: "Desactiva primero la categoría antes de eliminar el producto",
                    icon: "info"
                });
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.deleteLine({
                    slug: this.itemComponent.slug
                });
                this.$router.push("/app/lines");
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        blurName() {
            this.introValidators.name = true;
            this.setSlug();
        },
        validateData() {
            const toComplete = [];

            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidShortDescription) {
                toComplete.push("Descripción");
            }

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Categoría invalida",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }

            return true;
        },
        getDataSend() {
            return {
                slug: this.itemComponent.slug,
                name: this.itemComponent.name,
                summary: this.itemComponent.summary,
                active: this.itemComponent.active,
                highlight: this.itemComponent.highlight,
                highlightType: this.itemComponent.highlight
                    ? "highlight"
                    : null,
                parentSlug: this.itemComponent.line?.slug || "",
                images: this.images.map((item) => ({
                    id: item.id,
                    sequence: item.sequence.sequence
                }))
            };
        },
        getContentDataSend() {
            return {
                slug: this.itemComponent.slug,
                content: this.itemComponent.content,
                keywords: this.itemComponent.keywords
            };
        },
        async updateLineContentComponent() {
            this.isLoadingContent = true;
            this.$loading.show();
            try {
                await this.updateLine(this.getContentDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoadingContent = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoadingContent = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async loadSublines() {
            this.isLoadingSublines = true;
            try {
                this.sublines = await this.listSublinesByLine({
                    lineSlug: this.slug
                });
                this.isLoadingSublines = false;
            } catch (error) {
                this.isLoadingSublines = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async loadHighlightReferences() {
            this.isLoadingHighlightReferences = true;
            try {
                this.highlightReferences = await this.listHighlightReferences({
                    lineSlug: this.slug,
                    type: this.highlightReferencesType
                });
                this.isLoadingHighlightReferences = false;
            } catch (error) {
                this.isLoadingHighlightReferences = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        pushHighlightReference(reference) {
            const index = this.highlightReferences.findIndex(
                (item) => item.id === reference.id
            );
            if (index === -1) {
                this.highlightReferences.unshift(reference);
            }
        },
        async updateHighlightReferencesLine() {
            this.isLoadingHighlightReferences = true;
            try {
                await this.updateHighlightReferences({
                    lineSlug: this.slug,
                    type: this.highlightReferencesType,
                    referencesIds: this.highlightReferences.map(
                        (item) => item.id
                    )
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingHighlightReferences = false;
            } catch (error) {
                this.isLoadingHighlightReferences = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
